// Closed call-type vocabulary, code-owned routing rules, and wire contracts for
// capability-driven care scheduling. Call types are engineering-owned; coverage
// and policy rows are manager-editable data (spec §1).

import { z } from 'zod';
import { careTeamRuleRequestSchema, careTeamRuleSchema } from './care-team-rules';
import { welcomePoolPreviewSchema } from './welcome-pool-preview';
import { apiEnvelope } from '../envelope';
import { ClinicianRole } from '../programs/schema';
import { US_STATES } from '../registration/states';
import { CARE_APPOINTMENT_MODALITY, type CareAppointmentModality } from './appointments';

/** Every call-type value the product may reference — including reserved, non-routable keys. */
export const CARE_CALL_TYPE = [
  'welcome_call_substance',
  'welcome_call_tobacco',
  'welcome_call_behavioral',
  'welcome_call_low_no',
  'welcome_call_sud',
  'treatment_plan',
  'provider_call',
  'follow_up',
  'behavioural_health',
  'member_support',
  'progress_purpose_checkin',
] as const;
export type CareCallType = (typeof CARE_CALL_TYPE)[number];
export const careCallTypeSchema = z.enum(CARE_CALL_TYPE);

/** Programme-configurable welcome call types (subset of routable first-call types). */
export const WELCOME_CARE_CALL_TYPES = [
  'welcome_call_substance',
  'welcome_call_tobacco',
  'welcome_call_behavioral',
] as const;
export type WelcomeCareCallType = (typeof WELCOME_CARE_CALL_TYPES)[number];
export const welcomeCareCallTypeSchema = z.enum(WELCOME_CARE_CALL_TYPES);

/** Journey and pool call types managers toggle on the coverage matrix. */
export const COVERAGE_MATRIX_CALL_TYPES = [
  'welcome_call_substance',
  'welcome_call_tobacco',
  'welcome_call_behavioral',
  'welcome_call_low_no',
  'treatment_plan',
  'provider_call',
  'member_support',
  'progress_purpose_checkin',
] as const;
export type CoverageMatrixCallType = (typeof COVERAGE_MATRIX_CALL_TYPES)[number];

/** Pre–member-ops roster vocabulary — legacy GET …/care-staffing/roster contract (ADR-135). */
export const LEGACY_COVERAGE_MATRIX_CALL_TYPES = [
  'welcome_call_substance',
  'welcome_call_tobacco',
  'welcome_call_behavioral',
  'treatment_plan',
  'provider_call',
] as const satisfies readonly CoverageMatrixCallType[];
export type LegacyCoverageMatrixCallType = (typeof LEGACY_COVERAGE_MATRIX_CALL_TYPES)[number];

/** Every call type that resolves a bookable pool today. */
export const ROUTABLE_CARE_CALL_TYPES = [...COVERAGE_MATRIX_CALL_TYPES, 'follow_up'] as const;
export type RoutableCareCallType = (typeof ROUTABLE_CARE_CALL_TYPES)[number];

/** Frozen policy durations — the single source for seed, migration, and fixtures. */
/** Default Healthie contact type for telemedicine call types synced on deploy. */
export const DEFAULT_HEALTHIE_CONTACT_TYPE = 'Healthie Video Call' as const;

/** Healthie `available_contact_types` labels staff may set on call-type policies. */
export const HEALTHIE_CONTACT_TYPE_LABELS = [
  'Healthie Video Call',
  'Phone Call',
  'In Person',
] as const;
export type HealthieContactTypeLabel = (typeof HEALTHIE_CONTACT_TYPE_LABELS)[number];
export const healthieContactTypeSchema = z.enum(HEALTHIE_CONTACT_TYPE_LABELS);

export const CANONICAL_CARE_CALL_TYPE_DURATIONS: Record<RoutableCareCallType, number> = {
  welcome_call_substance: 60,
  welcome_call_tobacco: 30,
  welcome_call_behavioral: 60,
  welcome_call_low_no: 30,
  treatment_plan: 60,
  provider_call: 60,
  follow_up: 30,
  member_support: 30,
  progress_purpose_checkin: 40,
};
export const routableCareCallTypeSchema = z.enum(ROUTABLE_CARE_CALL_TYPES);

/** Values {@link getNextCall} places on `callType` (never `welcome_call_low_no`; see `offeredCallType`). */
export const NEXT_CALL_RESPONSE_CALL_TYPES = [
  'welcome_call_substance',
  'welcome_call_tobacco',
  'welcome_call_behavioral',
  'treatment_plan',
  'provider_call',
  'follow_up',
  'member_support',
  'progress_purpose_checkin',
] as const satisfies readonly RoutableCareCallType[];
export type NextCallResponseCallType = (typeof NEXT_CALL_RESPONSE_CALL_TYPES)[number];
export const nextCallResponseCallTypeSchema = z.enum(NEXT_CALL_RESPONSE_CALL_TYPES);

/** One bookable length of a call type, as the wire and the seed carry it. */
export type CareDurationOptionSeed = {
  readonly minutes: number;
  readonly default: boolean;
  readonly memberSelectable: boolean;
};

/**
 * Wire shape of one length a call type offers. Non-strict so a field added
 * later never breaks an installed client (ADR-135).
 */
export const careDurationOptionSchema = z.object({
  minutes: z.number().int().positive(),
  default: z.boolean(),
  memberSelectable: z.boolean(),
});
export type CareDurationOption = z.infer<typeof careDurationOptionSchema>;

const singleDefaultOption = (callType: RoutableCareCallType): readonly CareDurationOptionSeed[] => [
  { minutes: CANONICAL_CARE_CALL_TYPE_DURATIONS[callType], default: true, memberSelectable: true },
];

/**
 * The lengths each routable call type offers, ascending. Exactly one option per
 * call type is the default and its minutes equal the canonical duration. The
 * migration seeds `care_call_type_duration_options` from this vocabulary; a
 * drift-guard test keeps the two in step.
 */
export const CARE_CALL_TYPE_DURATION_OPTIONS: Record<
  RoutableCareCallType,
  readonly CareDurationOptionSeed[]
> = {
  welcome_call_substance: singleDefaultOption('welcome_call_substance'),
  welcome_call_tobacco: singleDefaultOption('welcome_call_tobacco'),
  welcome_call_behavioral: singleDefaultOption('welcome_call_behavioral'),
  welcome_call_low_no: singleDefaultOption('welcome_call_low_no'),
  treatment_plan: singleDefaultOption('treatment_plan'),
  provider_call: singleDefaultOption('provider_call'),
  follow_up: [
    { minutes: 15, default: false, memberSelectable: true },
    { minutes: 30, default: true, memberSelectable: true },
    { minutes: 45, default: false, memberSelectable: true },
    { minutes: 60, default: false, memberSelectable: true },
  ],
  member_support: singleDefaultOption('member_support'),
  progress_purpose_checkin: singleDefaultOption('progress_purpose_checkin'),
};

export type DurationOptionAudience = 'member' | 'staff';

export type DurationOptionResolution<Option extends CareDurationOption> =
  | { readonly ok: true; readonly option: Option }
  | { readonly ok: false; readonly reason: 'not-an-option' | 'not-member-selectable' };

/**
 * Decides which length a request books: the requested minutes when present,
 * else the call type's default. A requested length that is not an option, or is
 * staff-only on a member route, is rejected rather than silently replaced.
 */
export function resolveDurationOption<Option extends CareDurationOption>(
  options: readonly Option[],
  requested: number | undefined,
  audience: DurationOptionAudience,
): DurationOptionResolution<Option> {
  if (requested === undefined) {
    const fallback = options.find((option) => option.default);
    return fallback ? { ok: true, option: fallback } : { ok: false, reason: 'not-an-option' };
  }
  const match = options.find((option) => option.minutes === requested);
  if (!match) return { ok: false, reason: 'not-an-option' };
  if (audience === 'member' && !match.memberSelectable) {
    return { ok: false, reason: 'not-member-selectable' };
  }
  return { ok: true, option: match };
}

/**
 * An optional `callType` on a client-parsed response. A value this bundle does
 * not know reads as absent instead of failing the whole response, so adding a
 * call type never breaks an installed client that already carries this reader
 * (ADR-135 Rule 3).
 */
export const tolerantRoutableCareCallTypeSchema = z.preprocess((raw) => {
  if (raw === undefined) return undefined;
  const parsed = routableCareCallTypeSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}, routableCareCallTypeSchema.optional());
export const coverageMatrixCallTypeSchema = z.enum(COVERAGE_MATRIX_CALL_TYPES);

export const CARE_LICENCE_RULE = ['none', 'state-match'] as const;
export type CareLicenceRule = (typeof CARE_LICENCE_RULE)[number];

export const CARE_LICENCE_STATUS = ['active', 'inactive'] as const;
export type CareLicenceStatus = (typeof CARE_LICENCE_STATUS)[number];
export const careLicenceStatusSchema = z.enum(CARE_LICENCE_STATUS);

/** Substance readiness gate for state-match licence rules — read from programme config. */
export const CARE_LICENCE_READINESS = ['aud', 'oud'] as const;
export type CareLicenceReadiness = (typeof CARE_LICENCE_READINESS)[number];
export const careLicenceReadinessSchema = z.enum(CARE_LICENCE_READINESS);

/** Two-letter US state code stored on licence rows and matched against member state. */
export const usStateCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'State must be a two-letter uppercase code');
export type UsStateCode = z.infer<typeof usStateCodeSchema>;

/**
 * The jurisdictions a licence row may name, as two-letter USPS codes.
 *
 * Reused from the member address selector's list rather than retyped here,
 * because that list is what decides whether a licence can ever match anybody:
 * the `state-match` pool rule compares a licence's `state` against the member's
 * own stored state, and a member can only hold a state this list offers. A
 * licence recorded in a code outside it is therefore unbookable by
 * construction, not merely unusual — which is why a shape-only `^[A-Z]{2}$`
 * check is not enough at a write boundary: `ZZ`, `XX` and `UK` all pass it and
 * none of them can ever be matched.
 *
 * Fifty states plus DC, with no territory — exactly the reach the member
 * address screen has. Widening the product to a territory or outside the US
 * widens that list, and this gate follows it in the same change rather than
 * drifting behind a second copy.
 */
export const CARE_LICENCE_JURISDICTIONS: readonly string[] = US_STATES.map((state) => state.code);

const careLicenceJurisdictionSet = new Set<string>(CARE_LICENCE_JURISDICTIONS);

/** Whether `code` is a jurisdiction a care licence may name (see {@link CARE_LICENCE_JURISDICTIONS}). */
export function isCareLicenceJurisdiction(code: string): boolean {
  return careLicenceJurisdictionSet.has(code);
}

export type CareCallTypeClinicalRoles = 'any' | readonly z.infer<typeof ClinicianRole>[];

/** Counsellor-side roles allowed on welcome and treatment_plan coverage (not prescribers). */
export const NON_PRESCRIBER_COVERAGE_CLINICAL_ROLES = [
  'care_specialist',
  'counselor',
  'coordinator',
] as const satisfies readonly z.infer<typeof ClinicianRole>[];

export function clinicalRoleEligibleForCallType(
  clinicalRole: z.infer<typeof ClinicianRole>,
  callType: RoutableCareCallType,
): boolean {
  const allowed = CARE_CALL_TYPE_RULES[callType].clinicalRoles;
  if (allowed === 'any') return true;
  return allowed.includes(clinicalRole);
}

export type CareCallTypeRule = {
  readonly licence: CareLicenceRule;
  readonly clinicalRoles: CareCallTypeClinicalRoles;
  readonly assignmentRole: z.infer<typeof ClinicianRole>;
  readonly firstCall: boolean;
  /** When true, a completed welcome call of this type advances the member to treatment_plan. */
  readonly followedByTreatmentPlan: boolean;
  /** Visit modalities staff may pick when booking this call type (code-owned, Q2). */
  readonly modalities: readonly CareAppointmentModality[];
  /** Default Visit type when the dialog opens or the call type changes. */
  readonly defaultModality: CareAppointmentModality;
  /** When true, staff booking offers the member's care-team members in the pool before the rest of it. */
  readonly careTeamFirst?: true;
  /** When true, only staff may book or move this call; members can see and cancel it. */
  readonly staffBookedOnly?: true;
};

const VIDEO_AND_PHONE = ['video', 'phone'] as const satisfies readonly CareAppointmentModality[];
const ALL_VISIT_MODALITIES = CARE_APPOINTMENT_MODALITY satisfies readonly CareAppointmentModality[];

/** Whether `modality` is allowed for `callType` per the code-owned rule table. */
export function isModalityAllowed(
  callType: RoutableCareCallType,
  modality: CareAppointmentModality,
): boolean {
  return CARE_CALL_TYPE_RULES[callType].modalities.includes(modality);
}

/** Default visit modality for a routable call type. */
export function defaultModalityFor(callType: RoutableCareCallType): CareAppointmentModality {
  return CARE_CALL_TYPE_RULES[callType].defaultModality;
}

export const CARE_CALL_TYPE_RULES: Record<RoutableCareCallType, CareCallTypeRule> = {
  welcome_call_substance: {
    licence: 'none',
    clinicalRoles: NON_PRESCRIBER_COVERAGE_CLINICAL_ROLES,
    assignmentRole: 'care_specialist',
    firstCall: true,
    followedByTreatmentPlan: true,
    modalities: VIDEO_AND_PHONE,
    defaultModality: 'video',
  },
  welcome_call_tobacco: {
    licence: 'none',
    clinicalRoles: NON_PRESCRIBER_COVERAGE_CLINICAL_ROLES,
    assignmentRole: 'care_specialist',
    firstCall: true,
    followedByTreatmentPlan: false,
    modalities: VIDEO_AND_PHONE,
    defaultModality: 'video',
  },
  welcome_call_behavioral: {
    licence: 'none',
    clinicalRoles: NON_PRESCRIBER_COVERAGE_CLINICAL_ROLES,
    assignmentRole: 'care_specialist',
    firstCall: true,
    followedByTreatmentPlan: false,
    modalities: VIDEO_AND_PHONE,
    defaultModality: 'video',
  },
  welcome_call_low_no: {
    licence: 'none',
    clinicalRoles: ['care_specialist', 'counselor'],
    assignmentRole: 'care_specialist',
    firstCall: true,
    followedByTreatmentPlan: false,
    modalities: VIDEO_AND_PHONE,
    defaultModality: 'video',
  },
  treatment_plan: {
    licence: 'state-match',
    clinicalRoles: NON_PRESCRIBER_COVERAGE_CLINICAL_ROLES,
    assignmentRole: 'counselor',
    firstCall: false,
    followedByTreatmentPlan: false,
    modalities: ALL_VISIT_MODALITIES,
    defaultModality: 'video',
  },
  provider_call: {
    licence: 'state-match',
    clinicalRoles: ['prescriber', 'physician'],
    assignmentRole: 'prescriber',
    firstCall: false,
    followedByTreatmentPlan: false,
    modalities: ALL_VISIT_MODALITIES,
    defaultModality: 'video',
  },
  follow_up: {
    licence: 'none',
    clinicalRoles: 'any',
    assignmentRole: 'care_specialist',
    firstCall: false,
    followedByTreatmentPlan: false,
    modalities: ALL_VISIT_MODALITIES,
    defaultModality: 'video',
  },
  member_support: {
    licence: 'none',
    clinicalRoles: ['coordinator'],
    assignmentRole: 'coordinator',
    firstCall: false,
    followedByTreatmentPlan: false,
    modalities: VIDEO_AND_PHONE,
    defaultModality: 'video',
  },
  progress_purpose_checkin: {
    licence: 'none',
    clinicalRoles: ['care_specialist', 'counselor'],
    assignmentRole: 'care_specialist',
    firstCall: false,
    followedByTreatmentPlan: false,
    careTeamFirst: true,
    staffBookedOnly: true,
    modalities: ALL_VISIT_MODALITIES,
    defaultModality: 'video',
  },
};

export class CallTypeNotRoutableError extends Error {
  readonly callType: CareCallType;

  constructor(callType: CareCallType) {
    super(`Call type is not routable: ${callType}`);
    this.name = 'CallTypeNotRoutableError';
    this.callType = callType;
  }
}

const routableCareCallTypeSet = new Set<string>(ROUTABLE_CARE_CALL_TYPES);

function rosterCoverageCallType(entry: unknown): string | undefined {
  if (typeof entry !== 'object' || entry === null || !('callType' in entry)) return undefined;
  const callType = entry.callType;
  return typeof callType === 'string' ? callType : undefined;
}

/** Drop roster coverage rows whose call type this build does not route (ADR-135 Rule 3). */
export function dropRosterCoverageWithUnroutableCallTypes(raw: unknown): unknown {
  if (!Array.isArray(raw)) return raw;
  return raw.filter((entry) => {
    const callType = rosterCoverageCallType(entry);
    return callType !== undefined && routableCareCallTypeSet.has(callType);
  });
}

export function isRoutableCareCallType(callType: CareCallType): callType is RoutableCareCallType {
  return routableCareCallTypeSet.has(callType);
}

/** Hard failure for reserved or unknown call types — never an empty pool (AC-2). */
export function ruleFor(callType: CareCallType): CareCallTypeRule {
  if (!isRoutableCareCallType(callType)) {
    throw new CallTypeNotRoutableError(callType);
  }
  return CARE_CALL_TYPE_RULES[callType];
}

const welcomeCareCallTypeSet = new Set<string>(WELCOME_CARE_CALL_TYPES);

/** True for welcome-shaped call types (including Low/No) and the legacy `welcome_call_sud` alias. */
export function isWelcomeCareCallType(callType: string | null | undefined): boolean {
  if (!callType) return false;
  return (
    welcomeCareCallTypeSet.has(callType) ||
    callType === 'welcome_call_sud' ||
    callType === 'welcome_call_low_no'
  );
}

// ─── Wire schemas — manager API and next-call (no licence numbers on reads) ───

export const careCallTypePolicySchema = z.object({
  callType: routableCareCallTypeSchema,
  durationMinutes: z.number().int().positive(),
  highAcuityDurationMinutes: z.number().int().positive().nullable(),
  minNoticeMinutes: z.number().int().nonnegative(),
  horizonDays: z.number().int().positive(),
  cancellationNoticeMinutes: z.number().int().nonnegative(),
  healthieContactType: healthieContactTypeSchema,
  healthieAppointmentTypeId: z.string().min(1).nullable(),
});
export type CareCallTypePolicy = z.infer<typeof careCallTypePolicySchema>;

export const careSpecialistCoverageSchema = z.object({
  callType: routableCareCallTypeSchema,
  enabled: z.boolean(),
});
export type CareSpecialistCoverage = z.infer<typeof careSpecialistCoverageSchema>;

/** Read-side licence projection — never includes licence_number (AC-5). */
export const careSpecialistLicenceReadSchema = z.object({
  state: usStateCodeSchema,
  status: careLicenceStatusSchema,
  expiresAt: z.string().datetime({ offset: true }).nullable(),
  audReady: z.boolean(),
  oudReady: z.boolean(),
  licenceLevel: z.string().nullable(),
});
export type CareSpecialistLicenceRead = z.infer<typeof careSpecialistLicenceReadSchema>;

export const careStaffingRosterEntrySchema = z.object({
  specialistId: z.string().uuid(),
  // The staff member behind the specialist, for the link to their details page.
  // Absent from an API that predates it; the drawer then shows no link.
  internalUserId: z.string().uuid().optional(),
  displayName: z.string(),
  email: z.string().email().optional(),
  clinicalRole: ClinicianRole,
  coverage: z.preprocess(
    dropRosterCoverageWithUnroutableCallTypes,
    z.array(careSpecialistCoverageSchema),
  ),
  licences: z.array(careSpecialistLicenceReadSchema),
  // Absent from an API that predates the care_specialists.default_pool column.
  defaultPool: z.boolean().default(false),
  rules: z.array(careTeamRuleSchema).default([]),
});
export type CareStaffingRosterEntry = z.infer<typeof careStaffingRosterEntrySchema>;

export const careStaffingProgrammeCatalogueEntrySchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  /** When true, the programme is on an existing rule but not choosable for new selections (ADR-135). */
  inactive: z.boolean().optional(),
});
export type CareStaffingProgrammeCatalogueEntry = z.infer<
  typeof careStaffingProgrammeCatalogueEntrySchema
>;

export const careStaffingRosterDataSchema = z.object({
  specialists: z.array(careStaffingRosterEntrySchema),
  programmes: z.array(careStaffingProgrammeCatalogueEntrySchema).default([]),
});
export type CareStaffingRosterData = z.infer<typeof careStaffingRosterDataSchema>;

export const careStaffingRosterResponseSchema = apiEnvelope(careStaffingRosterDataSchema);
export type CareStaffingRosterResponse = z.infer<typeof careStaffingRosterResponseSchema>;

export const careStaffingCoveragePutRequestSchema = z.object({ enabled: z.boolean() }).strict();
export type CareStaffingCoveragePutRequest = z.infer<typeof careStaffingCoveragePutRequestSchema>;

export const careStaffingCoverageDataSchema = careSpecialistCoverageSchema;
export type CareStaffingCoverageData = z.infer<typeof careStaffingCoverageDataSchema>;

export const careStaffingCoverageResponseSchema = apiEnvelope(careStaffingCoverageDataSchema);
export type CareStaffingCoverageResponse = z.infer<typeof careStaffingCoverageResponseSchema>;

export const careStaffingDefaultPoolPutRequestSchema = z
  .object({
    enabled: z.boolean(),
    rules: z.array(careTeamRuleRequestSchema).optional(),
  })
  .strict();
export type CareStaffingDefaultPoolPutRequest = z.infer<
  typeof careStaffingDefaultPoolPutRequestSchema
>;

export const careStaffingDefaultPoolDataSchema = z.object({
  defaultPool: z.boolean(),
  rules: z.array(careTeamRuleSchema).default([]),
});
export type CareStaffingDefaultPoolData = z.infer<typeof careStaffingDefaultPoolDataSchema>;

export const careStaffingDefaultPoolResponseSchema = apiEnvelope(careStaffingDefaultPoolDataSchema);
export type CareStaffingDefaultPoolResponse = z.infer<typeof careStaffingDefaultPoolResponseSchema>;

// A call type's length and Healthie appointment type belong to its duration
// options (`care_call_type_duration_options`), which are seed- and
// migration-managed; the policy columns that mirror the default option are not
// writable here, so the options table has exactly one writer.
export const careStaffingPolicyPatchRequestSchema = careCallTypePolicySchema
  .omit({ callType: true, durationMinutes: true, healthieAppointmentTypeId: true })
  .partial()
  .strict()
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one policy field is required',
  });
export type CareStaffingPolicyPatchRequest = z.infer<typeof careStaffingPolicyPatchRequestSchema>;

export const careStaffingPolicyResponseSchema = apiEnvelope(careCallTypePolicySchema);
export type CareStaffingPolicyResponse = z.infer<typeof careStaffingPolicyResponseSchema>;

export const careStaffingLicencesPutRequestSchema = z
  .object({
    licences: z.array(careSpecialistLicenceReadSchema),
  })
  .strict()
  .refine(
    (body) => new Set(body.licences.map((licence) => licence.state)).size === body.licences.length,
    { message: 'Duplicate state codes are not allowed' },
  );
export type CareStaffingLicencesPutRequest = z.infer<typeof careStaffingLicencesPutRequestSchema>;

export const careStaffingLicencesDataSchema = z.object({
  licences: z.array(careSpecialistLicenceReadSchema),
});
export type CareStaffingLicencesData = z.infer<typeof careStaffingLicencesDataSchema>;

export const careStaffingLicencesResponseSchema = apiEnvelope(careStaffingLicencesDataSchema);
export type CareStaffingLicencesResponse = z.infer<typeof careStaffingLicencesResponseSchema>;

export const nextCallDataSchema = z.object({
  callType: nextCallResponseCallTypeSchema,
  durationMinutes: z.number().int().positive(),
  slots: z.array(z.string().datetime({ offset: true })),
  specialistName: z.string().min(1).nullable(),
  welcomePoolPreview: welcomePoolPreviewSchema.optional(),
  /** Server-derived offer; tolerant so OTA readers can branch without breaking the floor enum on `callType`. */
  offeredCallType: tolerantRoutableCareCallTypeSchema,
});
export type NextCallData = z.infer<typeof nextCallDataSchema>;

export const nextCallResponseSchema = apiEnvelope(nextCallDataSchema);
export type NextCallResponse = z.infer<typeof nextCallResponseSchema>;
